import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

const TEAM_SCOPE_MAP = {
  leads: "leads",
  businesses: "businesses",
  opportunities: "opportunities",
  tasks: "tasks",
  events: "events",
  contracts: "contracts",
  renewals: "renewals",
  commissions: "commissions",
  contacts: "contacts",
  activities: "activities",
  "host-businesses": "host_businesses",
};

function teamScopeId() {
  return localStorage.getItem("cc_crm_scope") || "";
}

function splitUrl(url = "") {
  const i = url.indexOf("?");
  return i === -1 ? [url, ""] : [url.slice(0, i), url.slice(i)];
}

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("cc_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;

  const scopeId = teamScopeId();
  const method = (cfg.method || "get").toLowerCase();
  const [path, query] = splitUrl(cfg.url || "");
  const resource = path.startsWith("/") ? path.slice(1) : path;
  const scopedResource = TEAM_SCOPE_MAP[resource];

  // Operational list screens are personal/team-scoped. The backend validates
  // whether the signed-in user is allowed to request the selected salesperson.
  if (scopeId && method === "get" && scopedResource) {
    cfg.url = `/team/resource/${scopedResource}${query}`;
    cfg.params = { ...(cfg.params || {}), user_id: scopeId };
  }

  // Completing/rescheduling a task must work even when the task has no linked
  // business/territory metadata (the original cause of Edward's missing tasks).
  if (scopeId && method === "put" && /^\/tasks\/[^/]+$/.test(path)) {
    cfg.url = `/team${path}${query}`;
  }

  // When the owner creates something while inside a salesperson's CRM, assign
  // it to that salesperson by default. Explicit form selections still win.
  if (scopeId && method === "post" && scopedResource && cfg.data && typeof cfg.data === "object") {
    if (!cfg.data.assigned_to) cfg.data.assigned_to = scopeId;
  }

  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const url = err.config?.url || "";
    const status = err.response?.status;
    // Only a genuine 401 (unauthenticated / expired / revoked) ends the session.
    // 403 is a PERMISSION problem and must NOT log the user out — pages handle it.
    if (status === 401 && !url.includes("/auth/")) {
      localStorage.removeItem("cc_token");
      const { pathname, search } = window.location;
      if (pathname !== "/login") {
        const next = encodeURIComponent(pathname + search);
        window.location.href = `/login?next=${next}`;
      }
    }
    return Promise.reject(err);
  }
);

export function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function downloadBlob(path, filename) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default api;
